import { TFunction } from 'i18next'
import _ from 'lodash'
import moment from 'moment'
import qs from 'qs'
import { Component, ComponentClass } from 'react'
import { default as slug } from 'slugify'
import { Helpers } from '@briq/shared'

import { BannerProps } from '../components/Banner'
import { SelectOption } from '../components/Select'
import { SearchFilter } from '../redux/config/types'
import { Domain, DomainData, DomainType } from '../redux/domains'
import { LogLevel } from '../redux/logs'

import { Banner, Dictionary } from './types'

export function resolveFilterValue(
  searchOptions: SearchFilter[],
  queryFilter?: undefined | string | string[] | qs.ParsedQs | qs.ParsedQs[]
) {
  return Helpers.resolveFilterValue(searchOptions, queryFilter)
}

export const filteredFilters = (filters: Dictionary<any>) =>
  _.reduce(
    filters,
    (acc, value, key) => {
      if (_.isEmpty(value)) return acc
      return {
        ...acc,
        [key]: value
      }
    },
    {}
  )

export function resolveQueryNumber(
  queryValue: undefined | string | string[] | qs.ParsedQs | qs.ParsedQs[],
  defaultValue: number
) {
  let value = _.isString(queryValue) ? _.parseInt(queryValue) : 1
  if (!_.isFinite(value)) value = defaultValue
  return value
}

export function resolveQueryString(queryValue: undefined | string | string[] | qs.ParsedQs | qs.ParsedQs[]) {
  return Helpers.resolveQueryString(queryValue)
}

export function mapActionError(e: any, level: LogLevel, path?: string) {
  return {
    message: _.isString(e) ? e : e.message,
    stack: _.isString(e) ? undefined : e.stack,
    level,
    path,
    additionalInfo: !_.isString(e) && e.isAxiosError ? JSON.stringify(e) : undefined
  }
}

interface ComponentEnhancer<TInner, TOutter> {
  (component: Component<TInner>): ComponentClass<TOutter>
}

/* istanbul ignore next */
export function compose<TInner, TOutter>(...functions: any[]): ComponentEnhancer<TInner, TOutter> {
  // eslint-disable-next-line lodash/prefer-lodash-method
  return functions.reduce(
    (a, b) =>
      (...args: any) =>
        a(b(...args)),
    _.identity
  )
}

export function createTFunction(prefix: string, t: TFunction) {
  return (key: string, params?: Dictionary<React.ReactText>) => t(prefix + key, params)
}

export type CTFunction = ReturnType<typeof createTFunction>

/* istanbul ignore next */
export function wait(delay: number) {
  return new Promise(resolve => setTimeout(() => resolve(undefined), delay))
}

export function getDomainDescription(
  domain: DomainType | undefined,
  code: string | undefined,
  domains?: Partial<Record<DomainType, Domain>>
) {
  if (!domains || !domain || !code) return ''

  // eslint-disable-next-line lodash/matches-shorthand
  return _.find(domains[domain], x => x.code === code)?.description || ''
}

export const slugify = (value: string, locale?: string) =>
  slug(value, {
    locale,
    lower: true,
    remove: /[*+~.()'"!:@]/g
  })

export function recordToPropertyBag(obj: Record<string, any>) {
  return _.map(obj, (val, key) => ({
    code: key,
    value: val
  }))
}

export function mapDomainDataToOptions(data: DomainData[]): SelectOption[] {
  return _.map(data, x => ({
    label: x.description,
    value: x.code
  }))
}

export function bannerDataToBannerProps(data: Banner): BannerProps {
  return {
    bannerCmsHtmlContent: data.description,
    title: data.title,
    link: data.link
      ? {
          url: data.link.url,
          target: data.link.target,
          text: data.link.text
        }
      : undefined
  }
}

export function getCompanyCountry(companyIdentifier: string): string {
  return _.toUpper(_.split(companyIdentifier, '-')[0])
}

export function formatDate(value: string | number | Date | undefined): string {
  return moment(value).format('YYYY-MM-DD')
}

export function getTextFromHtml(htmlString: string) {
  if (_.isEmpty(htmlString)) return ''
  const divContainer = document.createElement('div')
  divContainer.innerHTML = htmlString
  return divContainer.textContent || divContainer.innerText || ''
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}
